import React from 'react';
import {Button, ButtonGroup, Menu, MenuItem, Stack} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const statusButtonSx = {textTransform: 'none'};

/**
 * Action buttons for entity_recognition tasks.
 * When `inline` is true, renders as a compact vertical column (no top margin)
 * suitable for placement beside the card content.
 */
export function EntityRecognitionTaskButtons({
                                                 busy,
                                                 disabledReason,
                                                 onReject,
                                                 onSelectAnotherEntity,
                                                 onApply,
                                                 inline = false,
                                             }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLinkAnother = (event) => {
        onSelectAnotherEntity?.(event);
        handleMenuClose();
    };

    if (inline) {
        return (
            <Stack direction="column" spacing={0.5} alignItems="stretch" sx={{flexShrink: 0}}>
                <ButtonGroup variant="contained" size="small" orientation="vertical">
                    <Button
                        sx={{...statusButtonSx, fontSize: '0.7rem', py: 0.25}}
                        onClick={onApply}
                        disabled={busy || !!disabledReason}
                        title={disabledReason || 'Apply this entity mark'}
                    >
                        Confirm
                    </Button>
                    <Button
                        aria-controls={open ? 'apply-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        onClick={handleMenuClick}
                        disabled={busy || !!disabledReason}
                        sx={{py: 0.25, minWidth: 'auto'}}
                    >
                        <ArrowDropDownIcon fontSize="small"/>
                    </Button>
                </ButtonGroup>
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{...statusButtonSx, fontSize: '0.7rem', py: 0.25}}
                    onClick={onReject}
                    disabled={busy || !!disabledReason}
                    title={disabledReason || 'Reject this entity suggestion'}
                >
                    Reject
                </Button>

                <Menu
                    id="apply-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    slotProps={{paper: {'aria-labelledby': 'apply-button'}}}
                >
                    <MenuItem onClick={handleLinkAnother}>
                        Link another entity
                    </MenuItem>
                </Menu>
            </Stack>
        );
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{mt: 1}}>
            <Button
                size="small"
                variant="outlined"
                color="error"
                sx={statusButtonSx}
                onClick={onReject}
                disabled={busy || !!disabledReason}
                title={disabledReason || 'Reject this entity suggestion'}
            >
                Reject
            </Button>

            <ButtonGroup variant="contained" size="small">
                <Button
                    sx={statusButtonSx}
                    onClick={onApply}
                    disabled={busy || !!disabledReason}
                    title={disabledReason || 'Apply this entity mark to the document'}
                >
                    Confirm
                </Button>
                <Button
                    size="small"
                    aria-controls={open ? 'apply-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="menu"
                    onClick={handleMenuClick}
                    disabled={busy || !!disabledReason}
                    sx={{px: 0, minWidth: 'auto'}}
                >
                    <ArrowDropDownIcon/>
                </Button>
            </ButtonGroup>

            <Menu
                id="apply-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}

                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}

                slotProps={{
                    paper: {
                        'aria-labelledby': 'apply-button',
                    }
                }}
            >
                <MenuItem onClick={handleLinkAnother}>
                    Link another entity
                </MenuItem>
            </Menu>
        </Stack>
    );
}

/**
 * Default action buttons (Accept/Reject)
 */
export function DefaultTaskButtons({busy, disabledReason, onReject, onAccept}) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{mt: 1}}>
            <Button
                size="small"
                variant="outlined"
                color="error"
                sx={statusButtonSx}
                onClick={onReject}
                disabled={busy || !!disabledReason}
                title={disabledReason || ''}
            >
                Reject
            </Button>
            <Button
                size="small"
                variant="contained"
                sx={statusButtonSx}
                onClick={onAccept}
                disabled={busy || !!disabledReason}
                title={disabledReason || ''}
            >
                Accept
            </Button>
        </Stack>
    );
}

/**
 * Action buttons for compliance_check tasks
 */
export function ComplianceCheckTaskButtons({
                                               task,
                                               blockNode,
                                               busy,
                                               disabledReason,
                                               onReject,
                                               onAccept,
                                               onInsertText,
                                           }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleInsertText = () => {
        onInsertText?.(task, blockNode);
    };

    const handleSaveForLater = () => {
        onAccept?.();
        handleMenuClose();
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{mt: 1}}>
            <Button
                size="small"
                variant="outlined"
                color="error"
                sx={statusButtonSx}
                onClick={onReject}
                disabled={busy || !!disabledReason}
                title={disabledReason || 'Reject this suggestion'}
            >
                Reject
            </Button>

            {task?.suggestedText ? (
            <ButtonGroup variant="contained" size="small">
                <Button
                    sx={statusButtonSx}
                    onClick={handleInsertText}
                    disabled={busy || !!disabledReason}
                    title={disabledReason || 'Insert text into the document'}
                >
                    Replace Text
                </Button>
                <Button
                    size="small"
                    aria-controls={open ? 'compliance-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="menu"
                    onClick={handleMenuClick}
                    disabled={busy || !!disabledReason}
                    sx={{px: 0, minWidth: 'auto'}}
                >
                    <ArrowDropDownIcon/>
                </Button>
            </ButtonGroup>
            ) : null}

            <Menu
                id="compliance-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}

                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}

                slotProps={{
                    paper: {
                        'aria-labelledby': 'compliance-button',
                    }
                }}
            >
                <MenuItem onClick={handleSaveForLater}>
                    Save for later
                </MenuItem>
            </Menu>
        </Stack>
    );
}

